<template>
	<Skeleton :paragraph="{ rows: 10 }" :title="false" :loading="loading">
		<Form :model="formState" :label-col="{ span: 12 }" :wrapper-col="{ span: 12 }" :colon="false">
			<FormItem :label="$t('settings.vrsSettingsForm.brdcm.publishIntervalSec')">
				<Select
					v-model:value="formState.publish_interval_sec"
					:options="publishIntervalOptions"
					class="width-100"
				/>
			</FormItem>
			<FormItem :label="$t('settings.vrsSettingsForm.brdcm.cycleDelaySec')">
				<InputDecimal
					v-model="formState.cycle_delay_sec"
					range="all"
					:minValue="30"
					:maxValue="60"
					:allowPoint="false"
				/>
			</FormItem>
			<DividerAutoDirection>
				{{ $t('settings.vrsSettingsForm.brdcm.tableColumns.buff_len') }}
				<Tooltip placement="topLeft" color="black">
					<template #title>{{ $t('settings.vrsSettingsForm.brdcm.buffLenHint') }}</template>
					<QuestionCircleOutlined />
				</Tooltip>
			</DividerAutoDirection>
			<Row :gutter="16">
				<Col
					v-for="item in actualGnssData"
					:key="item.code"
					:xs="24"
					:sm="12"
					:lg="6"
				>
					<FormItem>
						<template #label>
							<GnssTag :code="item.code" />
						</template>
						<InputDecimal
							v-model="formState.buff_len[item.code]"
							range="all"
							:minValue="1"
							:maxValue="5"
							:allowPoint="false"
						/>
					</FormItem>
				</Col>
			</Row>
			<div class="scrollable-x pd-md-vertical">
				<Table
					class="brdcm-stations-table"
					size="small"
					:columns="stationColumns"
					:data-source="stationRows"
					:pagination="false"
					:scroll="stationTableScroll"
					row-key="id"
				>
					<template #headerCell="{ column }">
						<div v-if="column.dataIndex === 'station'" class="brdcm-station-header">
							<span>{{ column.title }}</span>
							<Input
								v-model:value="searchQuery"
								allowClear
								size="small"
								:placeholder="$t('settings.vrsSettingsForm.brdcm.stationsSearch')"
								@click.stop
							/>
						</div>
						<span v-else-if="column.dataIndex === 'ephemerides'">{{ column.title }}</span>
						<GnssTag v-else :code="column.dataIndex" />
					</template>
					<template #bodyCell="{ column, record }">
						<div v-if="column.dataIndex === 'station'" class="brdcm-station-cell">
							<Checkbox
								:checked="record.gnssAllChecked"
								:indeterminate="record.gnssIndeterminate"
								@change="onStationGnssAllChange(record.id)"
							/>
							<span>{{ record.stationLabel }}</span>
						</div>
						<div v-else-if="column.dataIndex === 'ephemerides'" class="brdcm-ephemerides-cell">
							<GnssTag
								v-for="badge in record.rtcmGnssBadges"
								:key="badge.code"
								:code="badge.code"
								:forceGrey="badge.forceGrey"
							/>
						</div>
						<LabeledSwitch
							v-else
							class="brdcm-gnss-switch"
							v-model="record.gnss[column.dataIndex]"
							small
						/>
					</template>
				</Table>
			</div>
			<div class="brdcm-all-stations">
				<Checkbox
					:checked="allStationsGnssCheckState.checked"
					:indeterminate="allStationsGnssCheckState.indeterminate"
					:disabled="!hasStations"
					@change="onAllStationsGnssChange"
				/>
				<span>{{ $t('settings.vrsSettingsForm.brdcm.toggleAllStations') }}</span>
			</div>
			<div class="brdcm-form-footer">
				<Button type="primary" @click.prevent="onSubmit" :loading="isSubmitting">
					{{ $t('settings.vrsSettingsForm.buttonLabel') }}
				</Button>
			</div>
		</Form>
	</Skeleton>
</template>

<script lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { Button, Checkbox, Col, Form, FormItem, Input, Row, Select, Skeleton, Table, Tooltip } from 'ant-design-vue';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';
import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';
import Consts from '@/api/Consts';
import Validators from '@/api/Validators';

import DividerAutoDirection from '@/components/common/DividerAutoDirection.vue';
import InputDecimal from '@/components/common/InputDecimal.vue';
import GnssTag from '@/components/common/GnssTag.vue';
import LabeledSwitch from '@/components/common/LabeledSwitch.vue';

import {
	ACTUAL_GNSS_DATA,
	applyBrdcmSettings,
	createBrdcmStationRows,
	createEmptyBrdcmFormState,
	ensureBrdcmStationSlots,
	getStationsGnssCheckState,
	serializeBrdcmFormState,
	toggleStationGnssAll,
	toggleStationsGnssAll,
	type BrdcmSettingsProp,
} from '@/components/settings/brdcmSettingsFormUtils';
import { useVRSSettingsPost } from '@/components/settings/useVRSSettingsPost.js';
import { useBaseStationsStore } from '@/stores/baseStations';
import { useProvideInjectFormChangedStatuses } from '@/utils/useProvideInject';


export default {
	components: {
		Button, Checkbox, Col, Form, FormItem, Input, Row, Select, Skeleton, Table, Tooltip,
		QuestionCircleOutlined,
		DividerAutoDirection, InputDecimal, GnssTag, LabeledSwitch,
	},
	props: {
		loading: {
			type: Boolean,
			required: true,
		},
		settings: {
			type: Object,
			required: true,
		},
	},
	setup(props: { loading: boolean; settings: BrdcmSettingsProp }) {
		const { t } = useI18n();
		const baseStationsStore = useBaseStationsStore();
		const searchQuery = ref('');
		const rtcmByStationId = ref<Record<number, number[]>>({});
		const formState = reactive(createEmptyBrdcmFormState());

		const activeStations = computed(() => (
			baseStationsStore.items.filter((item): item is typeof item & { mntpnt_int: string } => (
				item.deleted === false && item.mntpnt_int != null
			))
		));

		const { injectValue } = useProvideInjectFormChangedStatuses;
		const formChangedStatuses = injectValue();
		const { enableWatch, pauseWatch, resetChangedState } = Validators.useWatchFormChanges(
			formState,
			formChangedStatuses,
			'BrdcmSettingsForm',
		);

		const validate = () => Promise.resolve();
		const { isSubmitting, onSubmit } = useVRSSettingsPost(
			Api.vrsSettingsPost,
			formState,
			validate,
			serializeBrdcmFormState,
			resetChangedState,
		);

		watch(
			() => props.settings,
			value => {
				pauseWatch();
				applyBrdcmSettings(formState, value.brdcm, activeStations.value);
				nextTick(enableWatch);
			},
		);
		watch(
			activeStations,
			() => {
				ensureBrdcmStationSlots(formState, activeStations.value);
			},
		);

		onMounted(() => {
			Api.baseStationsNetInfoGet((response: { items?: { base_station: number; rtcm_messages: number[] }[] }) => {
				const items = response.items ?? [];
				const nextRtcmByStationId: Record<number, number[]> = {};
				for (const item of items) {
					nextRtcmByStationId[item.base_station] = item.rtcm_messages ?? [];
				}
				rtcmByStationId.value = nextRtcmByStationId;
			});
		});

		const publishIntervalOptions = computed(() => (
			Consts.BRDCM_PUBLISH_INTERVAL_SEC_CHOICES.map(value => ({
				value,
				label: t('settings.vrsSettingsForm.brdcm.publishIntervalOptions.' + String(value)),
			}))
		));

		const actualGnssData = ACTUAL_GNSS_DATA;
		const stationColumns = computed(() => [
			...Consts.brdcmStationsStationColumnComp.value,
			...Consts.BRDCM_STATION_GNSS_TABLE_COLUMNS,
		]);
		const stationTableScroll = {
			x: Consts.BRDCM_STATIONS_TABLE_SCROLL_X,
			y: 300,
		};

		const stationRows = computed(() => (
			createBrdcmStationRows(
				activeStations.value,
				formState.stations,
				searchQuery.value,
				rtcmByStationId.value,
			)
		));

		const hasStations = computed(() => activeStations.value.length > 0);

		const allStationsGnssCheckState = computed(() => (
			getStationsGnssCheckState(
				formState.stations,
				activeStations.value.map(station => station.id),
			)
		));

		const onStationGnssAllChange = (stationId: number) => {
			const gnss = formState.stations[stationId];
			if (gnss == null) {
				return;
			}
			toggleStationGnssAll(gnss);
		};

		const onAllStationsGnssChange = () => {
			toggleStationsGnssAll(
				formState.stations,
				activeStations.value.map(station => station.id),
			);
		};

		return {
			formState,
			searchQuery,
			isSubmitting,
			onSubmit,
			publishIntervalOptions,
			actualGnssData,
			stationColumns,
			stationTableScroll,
			stationRows,
			hasStations,
			allStationsGnssCheckState,
			onStationGnssAllChange,
			onAllStationsGnssChange,
		};
	},
};
</script>

<style scoped>
.brdcm-station-cell {
	display: flex;
	align-items: center;
	gap: 8px;
}

.brdcm-station-header {
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	align-items: center;
	gap: 8px;
	font-weight: 500;
	white-space: nowrap;
}

.brdcm-station-header :deep(.ant-input-affix-wrapper) {
	flex: 1 1 auto;
	min-width: 0;
}

.brdcm-ephemerides-cell {
	display: flex;
	flex-wrap: nowrap;
	justify-content: center;
	align-items: center;
	gap: 4px;
}

.brdcm-ephemerides-cell :deep(.gnss-tag) {
	margin-inline-end: 0;
}

.brdcm-stations-table :deep(th:not(:first-child)),
.brdcm-stations-table :deep(td:not(:first-child)) {
	text-align: center;
}

.brdcm-gnss-switch {
	display: flex;
	justify-content: center;
}

.brdcm-gnss-switch :deep(.switch-ctrl) {
	margin-inline-end: 0;
}

.brdcm-all-stations {
	display: flex;
	align-items: center;
	gap: 8px;
	padding-block: 8px 0;
	padding-inline-start: 8px;
}

.brdcm-form-footer {
	display: flex;
	justify-content: center;
	margin-block-start: 16px;
	padding-block-start: 16px;
	border-top: 1px solid #d9d9d9;
}
</style>
